import {
  AllPlugins,
  AhaEditorUtils,
  AhaEditorV2,
  type DocumentValue,
  type EditorPlugin,
  type AhaSlateEditor,
  HtmlSerializer,
} from '@aha-app/aha-editor-v2';
import '@aha-app/aha-editor-v2/dist/index.css';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type Rect } from '../types';
import { getPaddingCssFormat, stripEmptyHTML } from '../utils/html';
import { verticalAlignToFlex } from '../utils/verticalAlign';
import type EmbeddedTextBehaviors from './EmbeddedTextBehaviorsV2';

// The minimum width of an autogrow field.
const AUTO_GROW_MIN_WIDTH = 20;
const MIN_WIDTH = AUTO_GROW_MIN_WIDTH;

const WHITEBOARD_EDITOR_PLUGINS: EditorPlugin[] = [
  AllPlugins.MarkdownPlugin,
  AllPlugins.LinkPlugin,
  AllPlugins.InlinePlugin,
  AllPlugins.BoldPlugin,
  AllPlugins.ItalicPlugin,
  AllPlugins.UnderlinePlugin,
  AllPlugins.StrikethroughPlugin,
  AllPlugins.CodeSnippetPlugin,
  AllPlugins.ParagraphPlugin,
  AllPlugins.HeadersPlugin,
  AllPlugins.QuotePlugin,
  AllPlugins.CodeBlockPlugin,
  AllPlugins.TextColorPlugin,
  AllPlugins.DividerLinePlugin,
  AllPlugins.ListPlugin,
  AllPlugins.PasteHtmlPlugin,
  AllPlugins.PasteMarkdownPlugin,
  AllPlugins.PasteFragmentIntoListPlugin,
  AllPlugins.HotkeysPlugin,
  AllPlugins.FontSizePlugin,
].flat(10);

interface EditorV2ContainerProps {
  initialHTML: string;
  selectAll: boolean;
  setText: (text: string) => void;
  editorRect: Rect;
  object: fabric.Object & EmbeddedTextBehaviors;
  scale: number;
  editor: AhaSlateEditor;
  enterKeyEndsEditing?: boolean;
}

// React component for the editor during active editing.
const EditorV2Container: React.FC<EditorV2ContainerProps> = ({
  initialHTML,
  selectAll,
  setText,
  editorRect,
  object,
  scale,
  editor,
  enterKeyEndsEditing = false,
}) => {
  // We only deserialize the document on initial load. After that, we don't need to update the value, the AhaEditorV2
  // has its own state for the editor content.
  const value = useMemo<DocumentValue>(() => {
    // TODO: Maybe tell the serializer what set of plugins we'd like to use.
    const { children } = new HtmlSerializer(undefined, {
      preserveWhitespace: true,
      enableFontFamilyMark: true,
    }).deserialize(initialHTML);

    return { children };
  }, []);

  const editorMeasureRef = useRef<HTMLDivElement>(null);
  const [currentFontSize, setCurrentFontSize] = useState(
    object.fontSizeWithDefault()
  );
  const [currentTop, setCurrentTop] = useState(editorRect.top);
  const [currentLeft, setCurrentLeft] = useState(editorRect.left);
  const [currentHeight, setCurrentHeight] = useState(editorRect.height / scale);
  const [currentWidth, setCurrentWidth] = useState(editorRect.width / scale);
  const [isEmpty, setIsEmpty] = useState(false);
  const margin = object.scaledTextMargin();

  // Apply auto-size and auto-grow properties to text if it changed.
  const updateTextSize = async html => {
    // If editor is not mounted, we cannot measure the content. This is indicative
    // of a race condition between measuring the text after the latest change has
    // occurred and unmounting the editor. It is rare enough that it shouldn't
    // noticeably affect users most of the time.
    if (!editorMeasureRef.current) {
      return;
    }

    if (object.textProps.fontSize === 'scaleToFit') {
      await object.sizeTextToFit();
      setCurrentFontSize(object.fontSize);
    }

    if (
      ['user', 'growObject', 'growText', 'wrapGrow', 'wrapFixed'].includes(
        object.textProps.wrapText
      )
    ) {
      let newHeight = editorMeasureRef.current.clientHeight ?? MIN_WIDTH;
      let newWidth: number;
      if (!stripEmptyHTML(html)) {
        setIsEmpty(true);
        // Wrap fixed objects do not shrink
        if (object.textProps.wrapText === 'wrapFixed') {
          newWidth = object.textDimensions.width - margin.left - margin.right;
        } else {
          newWidth = AUTO_GROW_MIN_WIDTH;
        }
      } else {
        setIsEmpty(false);
        newWidth = editorMeasureRef.current.clientWidth ?? MIN_WIDTH;
      }
      newHeight += margin.top + margin.bottom;
      newWidth += margin.left + margin.right;
      const oldWidth = object.textDimensions.width;
      const oldHeight = object.textDimensions.height;

      if (object.textProps.wrapText === 'user' && object.wrapText) {
        newWidth = oldWidth;
      }

      if (object.textProps.wrapText === 'wrapGrow') {
        newWidth = newWidth < object.minWidth ? object.minWidth : newWidth;
        newHeight = newHeight < object.minHeight ? object.minHeight : newHeight;
      }
      // 'wrapFixed' shapes prioritize staying the same size, even if the text
      // does not necessarily take all the space available
      if (object.textProps.wrapText === 'wrapFixed') {
        newHeight = oldHeight > newHeight ? oldHeight : newHeight;
      }

      if (newWidth !== oldWidth || newHeight !== oldHeight) {
        const { top, left } = object.growText(newWidth - oldWidth);

        // wrapFixed objects behave a bit differently than other text types. They
        // grow vertically ,but their font size is fixed, and they remeasure their
        // text dimensions on scale. Currently, they do not support changing the
        // height directly, so to acommadate the increase in height, we adjust
        // the scale of the scaleY of the object instead.
        if (object.textProps.wrapText === 'wrapFixed') {
          object.set({
            top,
            left,
            scaleY: newHeight / object.height,
          });
        } else if (object.textProps.wrapText !== 'growText') {
          object.set({
            top,
            left,
            width: newWidth / object.scaleX,
            height: newHeight / object.scaleY,
          });
          object.textDimensions.width = newWidth;
          object.textDimensions.height = newHeight;
        } else {
          object.textDimensions.width = newWidth;
          object.textDimensions.height = newHeight;
        }
        const newRect = object.canvas.rectToWindowCoords(object.textPosition());

        object.synchronize();
        object.setCoords();
        object.canvas.requestRenderAll();

        setCurrentTop(newRect.top);
        setCurrentLeft(newRect.left);
        setCurrentWidth(newWidth);
        setCurrentHeight(newHeight);
      }
    }
  };

  useEffect(() => {
    AhaEditorUtils.Selection.selectAll(editor);
    if (!selectAll) {
      AhaEditorUtils.Selection.moveToEnd(editor);
    }
  }, [editor, selectAll]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && enterKeyEndsEditing) {
      e.preventDefault();
      e.stopPropagation();
      object.endTextEditing();
      object.controller.actionFocus(false);
    }
  };

  return (
    <div
      className={classnames('wb_editor', 'editing', {
        autoGrow:
          ['growObject', 'growText', 'user'].includes(
            object.textProps.wrapText
          ) && !object.wrapText,
      })}
      onKeyDown={handleKeyDown}
      style={
        {
          position: 'absolute',
          left: currentLeft,
          top: currentTop,
          minHeight: currentHeight,
          width:
            ['growObject', 'growText', 'user'].includes(
              object.textProps.wrapText
            ) && !object.wrapText
              ? undefined
              : currentWidth,
          minWidth: isEmpty ? currentWidth : undefined,
          transformOrigin: 'left top',
          transform: `rotate(${Number(object.angle || 0)}deg) scale(${scale})`,
          padding: getPaddingCssFormat(margin),
          boxSizing: 'border-box',
          fontSize: currentFontSize,
          opacity: Number(object.textOpacity || 1),
          textAlign: object.sanitizedTextAlign(),
          justifyContent: verticalAlignToFlex(object.sanitizedVerticalAlign()),
          '--editor-rotation': Number(object.angle || 0),
          '--aha-editor--user-font-size': `${currentFontSize}px`,
          '--aha-editor-v2--background-color': 'transparent',
          '--aha-editor-v2--content-padding': 0,
          '--aha-editor-v2--focused-border-color': 'transparent',
          '--aha-editor-v2--focused-box-shadow': 'none',
        } as React.CSSProperties
      }
    >
      <div
        ref={editorMeasureRef}
        className={classnames('content', object.textClass())}
      >
        <AhaEditorV2
          editor={editor}
          value={value}
          plugins={WHITEBOARD_EDITOR_PLUGINS}
          behaviors={{
            showBorder: false,
            disableAutoScroll: true,
            focusOnMount: true,
          }}
          collaborationOptions={{
            enabled: false,
          }}
          onChange={(children, editor) => {
            const html = new HtmlSerializer(editor).serialize();
            setText(html);

            // Updating the size immediately doesn't work correctly,
            // it measures the previous size.
            setTimeout(() => {
              void updateTextSize(html);
            });
          }}
        />
      </div>
    </div>
  );
};

export default EditorV2Container;
